/**
 * UpdateAvailableBanner
 *
 * Sits at the very top of the app shell when useAppVersion has detected a
 * newer deployed bundle than the one running in the user's tab. Distinct
 * from the existing toast notification — the toast can be dismissed and
 * never re-shown for that gitHash; this banner persists until the user
 * either reloads or explicitly snoozes.
 *
 * Why two notifications? Vaeth + the -3K-issue user (2026-06-05): both
 * sat on stale bundles for 2+ days while shipped fixes never reached
 * their browser. The toast either never fired (rate-limit / cooldown
 * already burned for their gitHash) or had been dismissed via the X
 * button. The banner is the always-on backstop.
 *
 * Visual: thin amber strip with Reload Now and a 15-minute Snooze. No
 * dismiss-forever button — that path already exists on the toast.
 */
import React, { useEffect, useState } from "react";
import { safeGetItem, safeSetItem } from "@mediatechgroup/app-version";

interface UpdateAvailableBannerProps {
  hasUpdate: boolean;
  isForceUpdate?: boolean;
  forceUpdateMessage?: string | null;
  onReload: () => void;
  onSnooze: () => void;
}

const SNOOZE_KEY = "app_update_banner_snooze_until";
const SNOOZE_DURATION_MS = 15 * 60 * 1000; // 15 minutes

const UpdateAvailableBanner: React.FC<UpdateAvailableBannerProps> = ({
  hasUpdate,
  isForceUpdate,
  forceUpdateMessage,
  onReload,
  onSnooze,
}) => {
  const [snoozedUntil, setSnoozedUntil] = useState<number>(() => {
    try {
      const stored = safeGetItem(SNOOZE_KEY);
      const parsed = stored ? parseInt(stored, 10) : 0;
      return Number.isFinite(parsed) ? parsed : 0;
    } catch {
      return 0;
    }
  });

  // Force a re-render when the snooze window expires so the banner reappears.
  useEffect(() => {
    if (!snoozedUntil || snoozedUntil <= Date.now()) return;
    const remaining = snoozedUntil - Date.now();
    const timer = setTimeout(() => setSnoozedUntil(0), remaining + 100);
    return () => clearTimeout(timer);
  }, [snoozedUntil]);

  if (!hasUpdate) return null;
  // Force updates ignore snooze — security/integrity.
  if (!isForceUpdate && snoozedUntil > Date.now()) return null;

  const handleSnooze = () => {
    const next = Date.now() + SNOOZE_DURATION_MS;
    try {
      safeSetItem(SNOOZE_KEY, String(next));
    } catch {
      // localStorage might be full; banner just won't persist the snooze,
      // which is the safer failure mode.
    }
    setSnoozedUntil(next);
    onSnooze();
  };

  const bg = isForceUpdate
    ? "bg-red-600 text-white"
    : "bg-amber-400 text-amber-950 dark:bg-amber-500 dark:text-amber-950";

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`w-full ${bg} px-4 py-2 flex flex-wrap items-center justify-center gap-3 text-sm font-medium shadow-sm`}
      // High z-index so it sits above sticky navigation, modal backdrops are higher
      // intentionally — modals shouldn't be blocked by an info banner.
      style={{ position: "sticky", top: 0, zIndex: 1000 }}
    >
      <span className="text-base" aria-hidden>
        🔄
      </span>
      <span>
        {isForceUpdate
          ? forceUpdateMessage ||
            "A critical update is available. Please reload to continue."
          : "A newer version of the app is available. Reload to apply the latest fixes."}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReload}
          className={`px-3 py-1 rounded font-semibold text-xs ${
            isForceUpdate
              ? "bg-white text-red-700 hover:bg-red-50"
              : "bg-amber-950 text-amber-50 hover:bg-amber-900"
          }`}
        >
          Reload Now
        </button>
        {!isForceUpdate && (
          <button
            type="button"
            onClick={handleSnooze}
            className="px-3 py-1 rounded text-xs underline hover:no-underline"
          >
            Snooze 15m
          </button>
        )}
      </div>
    </div>
  );
};

export default UpdateAvailableBanner;
